import { defineComponent as _defineComponent } from 'vue'
import { computed, ComputedRef, onMounted, ref, watch } from "vue";
import { DatePicker as VDatePicker } from "v-calendar";
import store from "@/vue/planning/store/store";
import {
  parseDateString,
  formatDateToString,
  formatDateRange,
  getDiffrenceDatesInNight,
} from "@/vue/planning/utils/datePickerHelpers";
import { AttributesRef, DateInfo, RangeType } from "@/types/datePicker";
import {
  getLanguageCode,
  getAdjacentMonth,
  parseHashParams,
} from "@/utils/utils";
import { HashParams } from "@/types";
import * as _ from "lodash";

import { onClickOutside } from "@vueuse/core";

// Dynamic Refs - Dates Handler

export default /*#__PURE__*/_defineComponent({
  __name: 'DatePickerV2',
  setup(__props) {

// Imports
const content = ref<any>(store.state.content);
const startDate = ref<Date | null>(
  parseDateString(store.state.dates["BeginDate"])
);
const endDate = ref<Date | null>(parseDateString(store.state.dates["EndDate"]));
const availableDates = ref<Date[]>([]);
const selectedDate = ref<Date | null>(startDate.value);
const prevSelectedDate = ref<Date | null>(null);
const currentMonth = ref<string>("");
const currentYear = ref<string>("");
const range = ref<RangeType>({ start: "", end: "" });
const attributesRef = ref<AttributesRef[]>([
  {
    key: "selected-dates",
    highlight: 'blue',
    dates: { start: new Date(), end: new Date() },
  },
]);

// Dynamic Refs - Elements Handler
const datePickerContainerRef = ref<HTMLElement | null>(null);
const datePickerRef = ref<InstanceType<typeof VDatePicker> | null>(null);
const isOpenedRef = ref<boolean>(false);
const selectedColor = ref("blue");

// Dynamic Refs - Miscellaneous
const isSelectingEndDateMode = ref<boolean>(false);
const generalParams = ref({
  locale: store.getters.locale,
  packCode: "",
  liheId: window.lihe.id,
});

const availabilitiesParams = ref({
  season: "",
});

const startDatesParams = ref({
  month: "",
});

const locale = ref(getLanguageCode(store.getters.locale));

//Computed
const departureText = computed((): string => {
  if (!startDate.value) return "";
  return formatDateRange(store.getters.locale, startDate.value);
});

const returnText = computed((): string => {
  if (!endDate.value) return "";
  return `${formatDateRange(store.getters.locale, endDate.value)} (${getDiffrenceDatesInNight(range.value)} ${content.value.nights})`;
});

// Methods
const handleSearch = (startDate: any, endDate: any) => {
  startDate.value = formatDateToString(startDate);
  endDate.value = formatDateToString(endDate);

  const datesObj = {
    BeginDate: startDate.value,
    EndDate: endDate.value,
  };

  store.dispatch("setDates", datesObj);
  isSelectingEndDateMode.value = false;

  toggleShowHideDatePicker(false);

  // Update the range ref
  range.value.start = startDate.value;
  range.value.end = endDate.value;

  // Update the attributesRef
  attributesRef.value[0].dates = {
    start: parseDateString(startDate.value),
    end: parseDateString(endDate.value),
  };

  // Display again the available start dates for the months in display
  const currentMonth = startDate.value.split("/")[1];
  fetchStartDates(currentMonth, true);
};

const selectDate = async (date: DateInfo): Promise<void> => {
  console.log(" selectDate ~ date:", date)
  if (date.isDisabled) return;

  if (isSelectingEndDateMode.value) {
    handleSearch(prevSelectedDate.value, selectedDate.value);
    return;
  }

  if(!selectedDate.value) return
  // First click in a valid date will turn isSelectingEndDateMode into 'true'
  isSelectingEndDateMode.value = true;

  // Update selectedDate
  prevSelectedDate.value = _.cloneDeep(selectedDate.value);
  selectedDate.value = parseDateString(date.id);

  // Fetch endDates
  startDatesParams.value.month = date.id.split("-").slice(0, 2).join("-");
  try {
    const res = await window.searchEngineService.getRelatedPackages(
      {
        ...generalParams.value,
        ...startDatesParams.value,
        startDate: date.id,
      },
      "getEndDates"
    );

    // Clean attributesRef and the availableDates section, and add the 'res' to availableDates it
    (attributesRef.value[0].dates = null), (availableDates.value = res);
  } catch (error) {
    console.error("Error fetching end dates at selectDate:", error);
  }
};

const toggleShowHideDatePicker = (show?: boolean) => {
  const pickerDiv = document.getElementById(
    "datePickerDiv"
  ) as HTMLElement | null;

  if (!pickerDiv || !datePickerContainerRef.value) return;

  if (typeof show == "undefined") show = !isOpenedRef.value;

  if (show) {
    setTimeout(() => {
      document.addEventListener("click", unfocuseDatePicker);
      pickerDiv.style.color = "var(--primary-blue)";
    }, 1);
  } else {
    document.removeEventListener("click", unfocuseDatePicker);
    pickerDiv.style.color = "black";
  }

  isOpenedRef.value = show;
};

const unfocuseDatePicker = (event: MouseEvent) => {
  if (
    event.target &&
    !(event.target as Element).closest(".datePickerContainerRef") &&
    !(event.target as Element).closest(".showDatePickerBtn")
  ) {
    toggleShowHideDatePicker(false);
  }
};

const moveDatePickerMonth = async (month: string, year: string) => {
  await datePickerRef.value.move({
    month: +month,
    year: +year,
  });
};

// Map and filter out null values, if they exist
const formatDatesArray = (array: string[]) => {
  const formattedArray = array
    .map((startDate: string) => parseDateString(startDate))
    .filter((date): date is Date => date !== null); // Type guard to ensure only valid Dates are kept;

  return formattedArray;
};

const handleMonthChange = (data: { month: number; year: number }): void => {
  if (!currentMonth.value || !currentYear.value) return;
  if (isSelectingEndDateMode.value) return;

  // Convert `data.month` to a 2-digit string with leading zero
  const formattedMonth = data.month.toString().padStart(2, "0");
  const formattedYear = data.year.toString();

  if (
    currentMonth.value === formattedMonth &&
    currentYear.value === formattedYear
  )
    return;

  currentYear.value = formattedYear;
  currentMonth.value = formattedMonth;

  fetchStartDates(currentMonth.value);
};

const fetchStartDates = async (
  monthToFetch: string,
  isHandleSearch?: boolean
): Promise<void | null> => {
  if (!currentYear.value || !monthToFetch) return null;

  const nextMonth: string = getAdjacentMonth(monthToFetch, "next");
  const prevMonth: string = getAdjacentMonth(monthToFetch, "prev");

  const monthsToFetch: string[] = [prevMonth, monthToFetch, nextMonth]

  try {
    // Fetch start dates for each month
    const startDatesPromises: Promise<string[]>[] = monthsToFetch.map(
      (month) => {
        startDatesParams.value.month = `${currentYear.value}-${month}`;
        return window.searchEngineService.getStartDates({
          ...generalParams.value,
          ...startDatesParams.value,
        });
      }
    );

    // Resolve all start dates
    const resolvedStartDates: string[][] = await Promise.all(
      startDatesPromises
    );

    // Format and collect all start dates
    const allStartDates = resolvedStartDates.flatMap((startDates) =>
      formatDatesArray(startDates)
    );

    // Remove duplicates and update availableDates
    availableDates.value = isHandleSearch ? 
    [
      ...new Set([...allStartDates]),
    ] :
    [
      ...new Set([...availableDates.value, ...allStartDates]),
    ];
  } catch (error) {
    console.error("Error fetching start dates at fetchStartDates:", error);
  }
};

/* handle initial data */
const preFill = async () => {
  if (!startDate.value || !endDate.value) return;
  if (!attributesRef.value[0].dates) return;

  // Set initial data
  attributesRef.value[0].dates.start = startDate.value;
  attributesRef.value[0].dates.end = endDate.value;

  range.value.start = store.state.dates["BeginDate"];
  range.value.end = store.state.dates["EndDate"];

  const beginDateFromStore: string  = store.state.dates["BeginDate"];
  const monthFromBeginDate: string = beginDateFromStore.split("/")[1];
  const yearFromBeginDate: string  = beginDateFromStore.split("/")[2];

  currentMonth.value = monthFromBeginDate;
  currentYear.value = yearFromBeginDate;

  // Fetch start dates
  fetchStartDates(currentMonth.value);

  // Movo calendar to relevant date
  moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
};

onMounted(() => {
  onClickOutside(datePickerContainerRef, () => toggleShowHideDatePicker(false))
})

// Watches
// Watch for changes in store.state.packCode and reset the search
watch(
  () => store.state.packCode, // Reactive source
  (newPackCode) => {
    // Parse the hash parameters
    const hashParams = parseHashParams() as HashParams;

    // Update params with new values for packCode and season
    generalParams.value.packCode = newPackCode;
    availabilitiesParams.value.season = hashParams.season;

    // Close the DatePicker, if it is open
    toggleShowHideDatePicker(false);

    // Prepare date
    const beginDateFromStore = store.state.dates["BeginDate"];
    const monthFromBeginDate = beginDateFromStore.split("/")[1];
    const yearFromBeginDate = beginDateFromStore.split("/")[2];

    // Fetch start dates
    fetchStartDates(currentMonth.value);

    // Movo calendar to relevant date
    moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
  }
);

watch(
  () => store.state.participants, // Reactive source
  (newBeginDate) => {
    // Close the DatePicker, if it is open
    toggleShowHideDatePicker(false);
  }
);

// Watch for changes in store.state.dates["BeginDate"] and update startDate
watch(
  () => store.state.dates["BeginDate"], // Reactive source
  (newBeginDate) => {
    startDate.value = parseDateString(newBeginDate); // Update startDate
  }
);

// Watch for changes in store.state.dates["EndDate"] and update endDate
watch(
  () => store.state.dates["EndDate"], // Reactive source
  (newEndDate) => {
    endDate.value = parseDateString(newEndDate); // Update endDate
  }
);


watch(
  () => store.state.loading, // Reactive source
  (newEndDate) => {
    preFill();
  }
);

return { __sfc: true,content, startDate, endDate, availableDates, selectedDate, prevSelectedDate, currentMonth, currentYear, range, attributesRef, datePickerContainerRef, datePickerRef, isOpenedRef, selectedColor, isSelectingEndDateMode, generalParams, availabilitiesParams, startDatesParams, locale, departureText, returnText, handleSearch, selectDate, toggleShowHideDatePicker, unfocuseDatePicker, moveDatePickerMonth, formatDatesArray, handleMonthChange, fetchStartDates, preFill, VDatePicker }
}

})